import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatIconModule } from '@angular/material/icon';
import { WorkorderSignaturePadComponent } from '../workorder-signature-pad/workorder-signature-pad.component';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DomSanitizer } from '@angular/platform-browser';
import { DrawerWorkorderActionsComponent } from '../drawers/drawer-workorder-actions/drawer-workorder-actions.component';
import { EmployeeVisitsByDay } from '../workorder-employee-visits/employee-visits-by-day';
import { EmployeeVisitsView } from '../workorder-employee-visits/employee-visits-view-enum';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { TranslateModule } from '@ngx-translate/core';
import { LocalizedDatePipe } from '../../app/shared/localizedDatePipe';

@Component({
  selector: 'app-workorder-signature-wizard',
  standalone: true,
  templateUrl: './workorder-signature-wizard.component.html',
  styleUrls: ['./workorder-signature-wizard.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatStepperModule,
    MatIconModule,
    WorkorderSignaturePadComponent,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    DrawerWorkorderActionsComponent,
    TranslateModule,
    LocalizedDatePipe,
  ],
})
export class WorkorderSignatureWizardComponent {
  @Input({ required: true }) employeeVisitsByDay!: EmployeeVisitsByDay[] | null;
  @Input({ required: true }) workOrderId!: number;
  @Output() employeeVisitsViewChange = new EventEmitter<EmployeeVisitsView>();
  @Output() enableSaveButton = new EventEmitter<boolean>();
  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild('employeeSignature') employeeSignature!: WorkorderSignaturePadComponent;
  @ViewChild('clientSignature') clientSignature!: WorkorderSignaturePadComponent;
  @ViewChild('fileUpload') fileUpload!: ElementRef;
  @ViewChild('stepper', { read: ElementRef })
  stepperElement!: ElementRef;

  employeeHasSignature = false;
  clientHasSignature = false;
  clientHasName = false;
  clientNameControl = new FormControl<string>('');

  maxFileSize = 5000000;
  uploadedFiles: File[] = [];
  maximumSizeError = false;
  numberofFilesError = false;

  observationsControl = new FormControl<string>('');

  constructor(private sanitizer: DomSanitizer) {}

  changeClientName() {
    this.clientHasName = this.clientNameControl.value != '';
  }

  clearEmployeeSignature() {
    this.employeeSignature.clearSignature();
  }

  clearClientSignature() {
    this.clientSignature.clearSignature();
  }

  onFileSelected(event: any) {
    this.numberofFilesError = false;
    this.maximumSizeError = false;

    let files = event.dataTransfer ? event.dataTransfer.files : event.target.files;
    if (this.uploadedFiles.length >= 3 || files.length > 3) {
      this.numberofFilesError = true;
      return;
    }

    for (let i = 0; i < files.length; i++) {
      let file = files[i];
      if (this.validateFile(file)) {
        file.objectURL = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(files[i]));
        this.uploadedFiles.push(files[i]);
      }
    }
  }

  removeFile(file: File) {
    let index = 0;
    if (this.uploadedFiles && -1 !== (index = this.uploadedFiles.indexOf(file))) {
      this.uploadedFiles.splice(index, 1);
      this.clearInputElement();
    }
  }

  clearInputElement() {
    this.fileUpload.nativeElement.value = '';
  }

  getEmployeeVisitsTotal() {
    return this.employeeVisitsByDay
      ? this.employeeVisitsByDay
          .map((ev) => ev.totalHours)
          .reduce(function (a, b) {
            return a + b;
          })
      : 0;
  }

  private validateFile(file: File) {
    const exists = this.uploadedFiles.some(
      (f) => f.name == file.name && f.lastModified == file.lastModified && f.size == file.size && f.type == file.type
    );

    this.maximumSizeError = file.size > this.maxFileSize || this.maximumSizeError;
    return !exists && file.size <= this.maxFileSize;
  }

  scrollToBottom() {
    let stepperContainer = this.stepperElement!.nativeElement;
    setTimeout(() => {
      stepperContainer.scrollTop = stepperContainer.scrollHeight;
    }, 250);
  }

  changeWizardStep(event: StepperSelectionEvent) {
    if (this.stepper) {
      event.selectedIndex == this.stepper.steps.length - 1
        ? this.enableSaveButton.emit(true)
        : this.enableSaveButton.emit(false);
    }
  }

  removeErrorMsg() {
    this.maximumSizeError = false;
    this.numberofFilesError = false;
  }

  exportEmployeeSignature() {
    const fileName = 'employeeSignature.png';
    return this.employeeSignature.exportSignature(fileName);
  }

  exportClientSignature() {
    const fileName = 'clientSignature.png';
    return this.clientSignature.exportSignature(fileName);
  }

  getAttachedFiles() {
    return this.uploadedFiles;
  }

  getObservations() {
    return this.observationsControl.value;
  }

  getClientSignerName() {
    return this.clientNameControl.value;
  }
}
